import type { Customer, PurchasedProductDetail } from "@/hooks/usePurchasedProductDetail";

const SKIP_SHOP_PAY = import.meta.env.VITE_SKIP_SHOP_PAY === "true";

/** Builds checkout prefill query string (email + shipping) for URL params */
export function buildCheckoutPrefillParams(customer: Partial<Customer>): URLSearchParams {
  const params = new URLSearchParams();
  if (customer.email) {
    params.append("checkout[email]", customer.email);
  }
  const { shipping_address } = customer;
  if (shipping_address) {
    if (shipping_address.first_name) {
      params.append("checkout[shipping_address][first_name]", shipping_address.first_name);
    }
    if (shipping_address.last_name) {
      params.append("checkout[shipping_address][last_name]", shipping_address.last_name);
    }
    if (shipping_address.address1) {
      params.append("checkout[shipping_address][address1]", shipping_address.address1);
    }
    if (shipping_address.address2) {
      params.append("checkout[shipping_address][address2]", shipping_address.address2);
    }
    if (shipping_address.city) {
      params.append("checkout[shipping_address][city]", shipping_address.city);
    }
    if (shipping_address.country) {
      params.append("checkout[shipping_address][country]", shipping_address.country);
    }
    if (shipping_address.province) {
      params.append("checkout[shipping_address][province]", shipping_address.province);
    }
    if (shipping_address.zip) {
      params.append("checkout[shipping_address][zip]", shipping_address.zip);
    }
    if (shipping_address.phone) {
      params.append("checkout[shipping_address][phone]", shipping_address.phone);
    }
  }
  return params;
}

/** Derives myshopify domain from shop_domain (e.g. www.drinkpurerose.com -> drinkpurerose.myshopify.com) */
function toMyshopifyDomain(shopDomain: string): string {
  if (shopDomain.includes(".myshopify.com")) {
    const host = shopDomain.replace(/^https?:\/\//, "").split("/")[0] ?? "";
    return host || shopDomain;
  }
  try {
    const host = shopDomain.replace(/^https?:\/\//, "").split("/")[0];
    const withoutWww = (host ?? "").replace(/^www\./, "");
    const handle = withoutWww.split(".")[0] ?? withoutWww;
    return `${handle}.myshopify.com`;
  } catch {
    return shopDomain;
  }
}

export function createSubscribeUrl(
  product: PurchasedProductDetail,
  variantId: number | string,
  quantity: number,
  sellingPlanId: string,
  discountCode?: string | null,
): string {
  const myshopify = toMyshopifyDomain(product.shopify_shop_domain);
  const base = `https://${myshopify}/cart/clear`;

  // Build checkout URL with email/shipping prefill for the return_to after cart/add
  const checkoutPrefill = buildCheckoutPrefillParams(product.customer);
  if (discountCode) {
    checkoutPrefill.append("discount", discountCode);
  }
  if (SKIP_SHOP_PAY) {
    checkoutPrefill.append("skip_shop_pay", "true");
  }
  const checkoutPath = checkoutPrefill.toString()
    ? `/checkout?${checkoutPrefill.toString()}`
    : "/checkout";

  const cartAddParams = new URLSearchParams({
    "items[][id]": String(variantId),
    "items[][quantity]": String(quantity),
    "items[][selling_plan]": sellingPlanId,
    "attributes[social_ocean_source]": "the-widget",
    return_to: checkoutPath,
  });
  const returnTo = `/cart/add?${cartAddParams.toString()}`;
  const params = new URLSearchParams({ return_to: returnTo });
  return `${base}?${params.toString()}`;
}

export function createCheckoutUrl(
  product: PurchasedProductDetail,
  selectedVariantId: number,
  quantity: number,
  discountCode?: string | null,
): string {
  const { customer, shop_domain } = product;
  const baseUrl = `https://${shop_domain}/cart/${selectedVariantId}:${quantity}`;
  const params = buildCheckoutPrefillParams(customer);
  if (discountCode) {
    params.append("discount", discountCode);
  }
  if (SKIP_SHOP_PAY) {
    params.append("skip_shop_pay", "true");
  }
  params.append("attributes[social_ocean_source]", "the-widget");
  const queryString = params.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}
